import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { PageHeader, DarkPanel, btnOnDark, SearchBox } from '../components/ui';

const RANGES = ['15M', '30M', '1H', '2H', '4H', '1D', '5D', '1M', '1Y', 'MAX'];

function Sparkline({ candles }) {
  const { pts, up, area } = useMemo(() => {
    const closes = (candles || []).map(c => Number(c.c)).filter(n => !Number.isNaN(n));
    if (closes.length < 2) return { pts: '', up: true, area: '' };
    const min = Math.min(...closes);
    const max = Math.max(...closes);
    const w = 640;
    const h = 200;
    const pad = 12;
    const points = closes.map((v, i) => {
      const x = pad + (i / (closes.length - 1)) * (w - pad * 2);
      const y = pad + (1 - (v - min) / (max - min || 1)) * (h - pad * 2);
      return [x, y];
    });
    const line = points.map(([x, y]) => `${x},${y}`).join(' ');
    const last = points[points.length - 1];
    const first = points[0];
    const areaPts = `${line} ${last[0]},${h - 4} ${first[0]},${h - 4}`;
    const isUp = closes[closes.length - 1] >= closes[0];
    return { pts: line, up: isUp, area: areaPts };
  }, [candles]);

  if (!pts) return null;

  const stroke = up ? '#34d399' : '#fb7185';
  const fill = up ? 'url(#gradUp)' : 'url(#gradDown)';

  return (
    <svg viewBox="0 0 640 200" className="w-full h-52">
      <defs>
        <linearGradient id="gradUp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="gradDown" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fb7185" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#fb7185" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon fill={fill} points={area} />
      <polyline
        fill="none"
        stroke={stroke}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={pts}
      />
    </svg>
  );
}

export default function StockPrice() {
  const { symbol: pathSymbol } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const querySymbol = searchParams.get('symbol');
  const { user } = useAuth();
  const { liveQuotePageEnabled, loading: settingsLoading } = useSettings();
  const isStaff = user?.role === 'ROLE_ADMIN' || user?.role === 'ROLE_SEMI_ADMIN';
  const quoteEnabled = isStaff || liveQuotePageEnabled;

  const initial = (pathSymbol || querySymbol || 'AAPL').toUpperCase();
  const [input, setInput] = useState(initial);
  const [sym, setSym] = useState(initial);
  const [quote, setQuote] = useState(null);
  const [chart, setChart] = useState(null);
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('1D');
  const symRef = useRef(sym);
  symRef.current = sym;

  const loadQuoteAndChart = async (symbol = symRef.current, r = range) => {
    if (!symbol || !quoteEnabled) return;
    setLoading(true);
    setChart(null);
    try {
      const [q, c] = await Promise.all([
        api.get(`/stock/price/${encodeURIComponent(symbol)}`).catch(() => ({ data: null })),
        api.get(`/stock/candles/${encodeURIComponent(symbol)}?range=${r}`).catch(() => ({ data: { success: false } })),
      ]);
      setQuote(q.data);
      setChart(c.data);
    } catch {
      setQuote({ success: false, message: 'Failed to fetch price' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const next = (pathSymbol || querySymbol || 'AAPL').toUpperCase();
    setInput(next);
    setSym(next);
  }, [pathSymbol, querySymbol]);

  useEffect(() => {
    if (!quoteEnabled) { setLoading(false); return; }
    loadQuoteAndChart(sym, range);
  }, [sym, range, quoteEnabled]);

  useEffect(() => {
    if (!quoteEnabled) return;
    const id = setInterval(() => loadQuoteAndChart(symRef.current, range), 120000); // every 2 min
    return () => clearInterval(id);
  }, [range, quoteEnabled]);

  useEffect(() => {
    if (!sym || !quoteEnabled) return;
    let cancelled = false;
    setNewsLoading(true);
    api
      .get(`/stock/news?tickers=${encodeURIComponent(sym)}&limit=5`)
      .then(r => {
        if (!cancelled) setNews(Array.isArray(r.data) ? r.data.slice(0, 5) : []);
      })
      .catch(() => {
        if (!cancelled) setNews([]);
      })
      .finally(() => {
        if (!cancelled) setNewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sym]);

  const goSymbol = (raw) => {
    const s = (raw || input || '').trim().toUpperCase();
    if (!s) return;
    setInput(s);
    setSym(s);
    if (pathSymbol) {
      navigate(`/stock/${encodeURIComponent(s)}`, { replace: true });
    } else {
      navigate(`/stock-price?symbol=${encodeURIComponent(s)}`, { replace: true });
    }
  };

  const chg = quote?.changePercent;
  const companyName = quote?.companyName || quote?.name;
  const showChart = !loading && chart?.success && Array.isArray(chart.candles) && chart.candles.length > 1;
  const up = showChart && Number(chart.candles[chart.candles.length - 1]?.c) >= Number(chart.candles[0]?.c);

  if (!settingsLoading && !quoteEnabled) {
    return (
      <div className="space-y-5 pb-8">
        <PageHeader title="Live Quote" />
        <DarkPanel bodyClassName="p-10 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-slate-400 text-xl mb-3">
            ⏸
          </div>
          <h1 className="text-lg font-semibold text-white">Live quote page unavailable</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-sm mx-auto">
            The live quote page is temporarily disabled by the admin. Signals and their prices are still
            visible on the Signals page.
          </p>
          <Link to="/signals" className={`${btnOnDark} mt-4`}>
            Back to Signals
          </Link>
        </DarkPanel>
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <PageHeader title="Live Quote" />

      <SearchBox
        value={input}
        onChange={setInput}
        onSubmit={goSymbol}
        placeholder="Search a symbol, e.g. AAPL"
        buttonLabel="Get quote"
        busy={loading}
      />

      {quote?.success && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold tracking-tight">{sym}</h2>
            {companyName && <p className="text-sm text-slate-500">{companyName}</p>}
          </div>
          <div className="text-right">
            <p className={`text-3xl font-bold tabular-nums ${Number(chg) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>${Number(quote.price).toFixed(2)}</p>
            <p className={`text-sm font-semibold ${Number(chg) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {Number(quote.change) >= 0 ? '+' : ''}
              {Number(quote.change).toFixed(2)} ({Number(chg) >= 0 ? '+' : ''}
              {Number(chg).toFixed(2)}%)
            </p>
          </div>
        </div>
      )}

      {quote && !quote.success && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-xl p-4 text-sm">
          <p className="font-medium">{quote.message || 'Failed to fetch price'}</p>
          <p className="mt-2 text-xs">Market data may be temporarily unavailable. Try again shortly.</p>
        </div>
      )}

      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {RANGES.map(r => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={`text-xs px-3 py-1.5 rounded-lg transition ${
              range === r ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {r}
          </button>
        ))}
      </div>

      {loading && !quote?.success && (
        <p className="text-center text-slate-400 py-10 text-sm">Loading…</p>
      )}

      {showChart && (
        <div
          className={`rounded-2xl border shadow-sm p-4 overflow-hidden ${
            up
              ? 'bg-gradient-to-br from-emerald-950 via-slate-900 to-slate-900 border-emerald-800/40'
              : 'bg-gradient-to-br from-rose-950 via-slate-900 to-slate-900 border-rose-800/40'
          }`}
        >
          <Sparkline candles={chart.candles} />
          {chart.source && (
            <p className="text-[10px] text-slate-400 text-right mt-1">
              {chart.source} · {chart.range}
            </p>
          )}
        </div>
      )}

      {!loading && !showChart && quote?.success && (
        <DarkPanel bodyClassName="p-8 text-center text-sm text-slate-400">
          Chart data unavailable for this range.
        </DarkPanel>
      )}

      {quote?.success && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Bid', quote.bid != null ? `$${Number(quote.bid).toFixed(2)}` : null],
            ['Ask', quote.ask != null ? `$${Number(quote.ask).toFixed(2)}` : null],
            ['Day high', quote.dayHigh != null ? `$${Number(quote.dayHigh).toFixed(2)}` : null],
            ['Day low', quote.dayLow != null ? `$${Number(quote.dayLow).toFixed(2)}` : null],
            ['Volume', quote.volume],
            ['Change', quote.change != null ? Number(quote.change).toFixed(2) : null],
          ].map(([label, val]) => (
            <div key={label} className="rounded-xl border border-slate-700/60 bg-gradient-to-br from-slate-900 to-slate-800 p-3 shadow-md">
              <p className="text-[10px] uppercase text-slate-400">{label}</p>
              <p className="font-semibold tabular-nums text-white">
                {val != null && val !== '' ? val : '—'}
              </p>
            </div>
          ))}
        </div>
      )}

      <DarkPanel
        title={`Latest news · ${sym}`}
        right={<span className="text-[10px] uppercase tracking-wide text-slate-400">Top 5</span>}
        bodyClassName="p-0"
      >
        {newsLoading && (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">Loading news…</p>
        )}
        {!newsLoading && news.length === 0 && (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">No recent news for {sym}.</p>
        )}
        {!newsLoading && news.length > 0 && (
          <ul className="divide-y divide-white/5">
            {news.map((n, i) => (
              <li key={`${n.url || n.title}-${i}`} className="px-4 py-3 hover:bg-white/[0.03] transition">
                <div className="flex gap-3">
                  {n.image ? (
                    <img
                      src={n.image}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover bg-slate-100 shrink-0 hidden sm:block"
                      onError={e => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    {n.url ? (
                      <a
                        href={n.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-white hover:text-emerald-300 line-clamp-2"
                      >
                        {n.title || 'Untitled'}
                      </a>
                    ) : (
                      <p className="text-sm font-semibold text-white line-clamp-2">{n.title || 'Untitled'}</p>
                    )}
                    {n.summary && (
                      <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.summary}</p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">
                      {[n.source, n.time].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DarkPanel>
    </div>
  );
}
